import axios from "./axios";

export interface ResendSummaryResponse {
  range: { days: number; fromUtc: string; toUtc: string };
  webhook: {
    total: number;
    byType: Array<{ eventType: string; count: number }>;
    funnel?: {
      delivered: number;
      bounced: number;
      complained: number;
      opened: number;
      clicked: number;
    };
  };
  mail: {
    total: number;
    success: number;
    failed: number;
    failedRecipients: Array<{
      email: string;
      failCount: number;
      lastFailureAtUtc: string;
    }>;
  };
}

export interface ResendEventItem {
  id: string;
  occurredAtUtc: string;
  area: string;
  action: string;
  severity: number;
  targetType?: string;
  targetId?: string;
  statusCode?: number;
  metadataJson?: string;
}

export interface TransactionalMailPreviewItem {
  eventType: string;
  displayName: string;
  source: string;
  subject: string;
  htmlBody: string;
  plainTextBody: string;
  sampleVariables: Record<string, string>;
}

export async function getResendSummary(days = 7): Promise<ResendSummaryResponse | null> {
  try {
    const res = await axios.get("/admin/resend/summary", { params: { days } });
    return res.data as ResendSummaryResponse;
  } catch (error) {
    console.error("getResendSummary error", error);
    return null;
  }
}

export async function getResendEvents(params: {
  days?: number;
  area?: string;
  eventType?: string;
  page?: number;
  pageSize?: number;
}): Promise<{ total: number; items: ResendEventItem[] } | null> {
  try {
    const res = await axios.get("/admin/resend/events", { params });
    return {
      total: Number(res.data?.total ?? 0),
      items: Array.isArray(res.data?.items) ? (res.data.items as ResendEventItem[]) : [],
    };
  } catch (error) {
    console.error("getResendEvents error", error);
    return null;
  }
}

export async function getResendConfigHealth(): Promise<any | null> {
  try {
    const res = await axios.get("/admin/resend/config-health");
    return res.data;
  } catch (error) {
    console.error("getResendConfigHealth error", error);
    return null;
  }
}

export async function getTransactionalMailPreviews(eventType?: string): Promise<TransactionalMailPreviewItem[]> {
  try {
    const res = await axios.get("/admin/resend/transactional-previews", {
      params: eventType ? { eventType } : undefined,
    });

    const items = res.data?.items;
    if (Array.isArray(items)) {
      return items as TransactionalMailPreviewItem[];
    }

    if (res.data?.item && typeof res.data.item === "object") {
      return [res.data.item as TransactionalMailPreviewItem];
    }

    return [];
  } catch (error) {
    console.error("getTransactionalMailPreviews error", error);
    return [];
  }
}

export async function listResendTemplates(): Promise<any[]> {
  try {
    const res = await axios.get("/admin/resend/templates");
    const data = res.data?.data;
    if (Array.isArray(data?.data)) {
      return data.data;
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  } catch (error) {
    console.error("listResendTemplates error", error);
    return [];
  }
}

export async function getResendTemplate(templateId: string): Promise<any | null> {
  try {
    const res = await axios.get(`/admin/resend/templates/${templateId}`);
    return res.data?.data ?? null;
  } catch (error) {
    console.error("getResendTemplate error", error);
    return null;
  }
}

export async function createResendTemplate(payload: any): Promise<boolean> {
  try {
    await axios.post("/admin/resend/templates", payload);
    return true;
  } catch (error) {
    console.error("createResendTemplate error", error);
    return false;
  }
}

export async function updateResendTemplate(templateId: string, payload: any): Promise<boolean> {
  try {
    await axios.put(`/admin/resend/templates/${templateId}`, payload);
    return true;
  } catch (error) {
    console.error("updateResendTemplate error", error);
    return false;
  }
}

export async function publishResendTemplate(templateId: string): Promise<boolean> {
  try {
    await axios.post(`/admin/resend/templates/${templateId}/publish`);
    return true;
  } catch (error) {
    console.error("publishResendTemplate error", error);
    return false;
  }
}

export async function deleteResendTemplate(templateId: string): Promise<boolean> {
  try {
    await axios.delete(`/admin/resend/templates/${templateId}`);
    return true;
  } catch (error) {
    console.error("deleteResendTemplate error", error);
    return false;
  }
}

export async function duplicateResendTemplate(templateId: string, name?: string): Promise<boolean> {
  try {
    await axios.post(`/admin/resend/templates/${templateId}/duplicate`, { name });
    return true;
  } catch (error) {
    console.error("duplicateResendTemplate error", error);
    return false;
  }
}

export async function sendResendTemplateTest(templateId: string, to: string, variables?: Record<string, unknown>): Promise<boolean> {
  try {
    await axios.post(`/admin/resend/templates/${templateId}/test-send`, { to, variables });
    return true;
  } catch (error) {
    console.error("sendResendTemplateTest error", error);
    return false;
  }
}

export async function getResendDomains(): Promise<any[]> {
  try {
    const res = await axios.get("/admin/resend/domains");
    const data = res.data?.data;
    if (Array.isArray(data?.data)) {
      return data.data;
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  } catch (error) {
    console.error("getResendDomains error", error);
    return [];
  }
}

export async function createResendDomain(name: string): Promise<boolean> {
  try {
    await axios.post("/admin/resend/domains", { name });
    return true;
  } catch (error) {
    console.error("createResendDomain error", error);
    return false;
  }
}

export async function verifyResendDomain(domainId: string): Promise<boolean> {
  try {
    await axios.post(`/admin/resend/domains/${domainId}/verify`);
    return true;
  } catch (error) {
    console.error("verifyResendDomain error", error);
    return false;
  }
}

export async function listResendContacts(): Promise<any[]> {
  try {
    const res = await axios.get("/admin/resend/contacts");
    const data = res.data?.data;
    if (Array.isArray(data?.data)) {
      return data.data;
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  } catch (error) {
    console.error("listResendContacts error", error);
    return [];
  }
}

export async function upsertResendContact(payload: any): Promise<boolean> {
  try {
    await axios.post("/admin/resend/contacts/upsert", payload);
    return true;
  } catch (error) {
    console.error("upsertResendContact error", error);
    return false;
  }
}

export async function listResendSegments(): Promise<any[]> {
  try {
    const res = await axios.get("/admin/resend/segments");
    const data = res.data?.data;
    if (Array.isArray(data?.data)) {
      return data.data;
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  } catch (error) {
    console.error("listResendSegments error", error);
    return [];
  }
}

export async function listResendTopics(): Promise<any[]> {
  try {
    const res = await axios.get("/admin/resend/topics");
    const data = res.data?.data;
    if (Array.isArray(data?.data)) {
      return data.data;
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  } catch (error) {
    console.error("listResendTopics error", error);
    return [];
  }
}

export async function runResendBroadcastPilot(payload: {
  approved: boolean;
  dryRun: boolean;
  audienceId: string;
  name: string;
  subject: string;
  html: string;
  text?: string;
}): Promise<boolean> {
  try {
    await axios.post("/admin/resend/broadcasts/pilot", payload);
    return true;
  } catch (error) {
    console.error("runResendBroadcastPilot error", error);
    return false;
  }
}
